import React, { useCallback, useEffect, useRef, useState } from 'react';
import loadable from '@loadable/component';
import { useQuery } from '@apollo/client';

import { SceneContainer } from '..';
import { GeolocationContext } from '../context';
import { useLayer } from '../LayerContext/useLayer';
import { getItem } from '../../io';
import { useSettings } from '../context/SettingsContext/useSettings';
import { useWorkspace } from '../context/WorkspaceContext/useWorkspace';
import { DrawerPuller } from './DrawerPuller';
import { ShareMapDialog } from '../ShareMapDialog';
import { AuthMenuButton } from '../AuthMenuButton';
import { AUTH_QUERY } from '../../gql';
import { resolveAccessibleLayer } from '../enums';
import { useMapShortcuts } from '../MapView/hooks/useMapShortcuts';

const MapControls = loadable(
  () => import('../MapControls').then((mod) => mod.MapControls),
  { ssr: false }
);

const MapToolsMenu = loadable(
  () => import('../MapToolsMenu').then((mod) => mod.MapToolsMenu),
  { ssr: false }
);

const MapView = loadable(
  () => import('../MapView').then((mod) => mod.MapView),
  { ssr: false }
);

const Scene = ({
  isMobile = false,
  isPublic = false,
  allowedLayers,
}: {
  isMobile?: boolean;
  isPublic?: boolean;
  allowedLayers?: string[];
}) => {
  const { layer } = useLayer();
  const {
    store: {
      activeFindFilter,
      findLayersEnabled,
      heatmapLayerEnabled,
      isadLayerEnabled,
      lpisLayerEnabled,
      objectLayerEnabled,
      findVisualization,
      hideVisualizationDefaults,
    },
    setSelectedEntity,
    toggleMapDrawer,
  } = useSettings();
  const {
    store: { sharedInitialCoordinates, markerPoints },
    onClickMap,
    setSharedMap,
  } = useWorkspace();
  const [geolocate, setGeolocate] = useState(false);
  const { data: authData, loading: authLoading } = useQuery(AUTH_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const premiumLayersAvailable = isPublic
    ? false
    : authLoading
    ? undefined
    : authData?.viewer?.user?.role === 'admin' ||
      authData?.viewer?.premiumAccess?.isPremium === true;
  const selectedLayer = resolveAccessibleLayer(
    layer,
    premiumLayersAvailable === true
  );

  const sceneContainerRef = useRef<HTMLDivElement>(null);
  useMapShortcuts(sceneContainerRef);

  useEffect(() => {
    const container = sceneContainerRef.current;

    if (container) {
      const resizeObserver = new ResizeObserver(() => {
        window.dispatchEvent(new Event('resize')); // Deck.gl's resize listener
      });

      resizeObserver.observe(container);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  const toggleMapDrawerHandle = () => {
    toggleMapDrawer();
    window.dispatchEvent(new Event('resize'));
  };
  const handleGeolocate = useCallback(() => {
    setGeolocate(true);
  }, []);

  const clearInitialization = () =>
    setSharedMap({ sharedInitialCoordinates: null });

  const initialCoordinates =
    typeof window !== 'undefined'
      ? sharedInitialCoordinates || getItem('initialCoordinates')
      : null;

  return (
    <GeolocationContext.Provider value={geolocate}>
      <SceneContainer ref={sceneContainerRef} isMobile={isMobile}>
        <MapControls
          allowedLayers={allowedLayers}
          premiumLayersAvailable={premiumLayersAvailable}
        />
        {isPublic ? <AuthMenuButton /> : <MapToolsMenu />}
        <MapView
          layer={selectedLayer}
          filters={isPublic ? null : activeFindFilter}
          findLayersEnabled={isPublic ? false : findLayersEnabled}
          heatmapLayerEnabled={isPublic ? false : heatmapLayerEnabled}
          objectLayerEnabled={isPublic ? false : objectLayerEnabled}
          findVisualization={findVisualization}
          hideVisualizationDefaults={hideVisualizationDefaults}
          onSelectEntity={setSelectedEntity}
          isadLayerEnabled={isPublic ? false : isadLayerEnabled}
          lpisLayerEnabled={
            !isPublic && premiumLayersAvailable === true && lpisLayerEnabled
          }
          disableUserData={isPublic}
          onClickMap={isPublic ? undefined : onClickMap}
          markerPoints={markerPoints}
          initialCoordinates={initialCoordinates}
          onLoad={clearInitialization}
          onGeolocate={handleGeolocate}
          geolocationActive={geolocate}
          copernicusEnabled={premiumLayersAvailable === true}
        />
        {!isPublic && !isMobile && (
          <DrawerPuller onClick={toggleMapDrawerHandle} />
        )}
      </SceneContainer>
      {!isPublic && <ShareMapDialog />}
    </GeolocationContext.Provider>
  );
};

export { Scene };
